/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Footer Component
 * --------
 * A sleek, responsive footer with branding, social links, and credits.
 */

import React from 'react';
import { useTheme } from '../theme/ThemeContext';

const ArceeLogo = ({ color, primaryColor }) => (
    <svg width="100" height="24" viewBox="0 0 100 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 6, display: 'inline-block', verticalAlign: 'middle' }}>
        <text x="0" y="18" fill={primaryColor} fontFamily="Inter, sans-serif" fontWeight="600" fontSize="20">arcee</text>
        <text x="54" y="18" fill={color} fontFamily="Inter, sans-serif" fontWeight="600" fontSize="20">.io</text>
    </svg>
);

const RudraLogo = ({ primaryColor }) => (
    <svg width="180" height="40" viewBox="0 0 280 54" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Icon on left */}
        <rect x="2" y="2" width="60" height="14" rx="5" fill={primaryColor} fillOpacity="0.8"></rect>
        <rect x="2" y="20" width="60" height="14" rx="5" fill={primaryColor} fillOpacity="0.8"></rect>
        <rect x="2" y="38" width="60" height="14" rx="5" fill={primaryColor} fillOpacity="0.8"></rect>
        <circle cx="32" cy="27" r="6" fill="#ff811a"></circle>

        {/* Text on right with thin font */}
        <text x="75" y="30" fill={primaryColor} fillOpacity="0.9" fontFamily="Arial, sans-serif" fontWeight="100" fontSize="37">RUDRA</text>
        <text x="75" y="52" fill={primaryColor} fillOpacity="0.9" fontFamily="Arial, sans-serif" fontWeight="100" fontSize="24">CORESOFT</text>
    </svg>
);

export default function Footer() {
    const { theme } = useTheme();

    return (
        <footer
            style={{
                marginTop: 'auto',
                borderTop: '1px solid #f0f0f0',
                background: '#ffffff',
                padding: '24px 0 16px',
                fontSize: '0.9rem',
                color: '#666',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}>
                Powered by
                <a
                    href="https://arcee.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                >
                    <ArceeLogo color={theme.secondary} primaryColor={theme.primary} />
                </a>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.85rem', color: '#888' }}>Developed by</span>
                <a
                    href="https://rudracoresoft.com"
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'block', transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                    <RudraLogo primaryColor={theme.primary} />
                </a>
            </div>

            <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 8 }}>
                @2025 Rudra Coresoft Technologies Private Limited. All rights reserved
            </div>
        </footer>
    );
}
